import { ReactNode, useEffect, useState } from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import { DataService } from 'api/DataService'
import SourceForm from 'templates/AccountsPayable/components/ConfigurationDrawer/Steps/SourceForm'

import AutocompleteAdd from 'components/AutocompleteAdd/AutocompleteAdd'

import { theme } from 'styles/theme'

type Record = {
  label: string
  value: string
  source_type: string
}

type SourceAutocompleteProps<T extends FieldValues> = {
  setValue: UseFormSetValue<T>
  value: Omit<Record, 'source_type'>
  onChange: any
  errorMessage: string
  label?: string
  // Ressalva opcional ao lado do rótulo, no ícone de informação. Só aparece
  // quando informada — o campo de origem da despesa continua sem nenhuma.
  labelTooltip?: ReactNode
  placeholder?: string
  addButtonText?: string
  modalTitle?: string
  /**
   * Nome da entidade na tela que está usando o autocomplete ("Origem" em Contas
   * a Pagar, "Conta" em Contas a Receber). Vai para o formulário de criação, que
   * é o mesmo nos dois lados — sem isso o modal se chama "Nova conta" e o
   * diálogo de dentro pergunta "Confirma criar uma Origem?".
   */
  entityLabel?: string
}

const SourceAutocomplete = <T extends FieldValues>({
  value,
  onChange,
  errorMessage,
  setValue,
  label: customLabel,
  labelTooltip,
  entityLabel,
  placeholder: customPlaceholder,
  addButtonText: customAddButtonText,
  modalTitle: customModalTitle
}: SourceAutocompleteProps<T>) => {
  const [sources, setSources] = useState<Record[]>()
  // Incrementa depois de criar um item novo pelo "Adicionar", pra lista ser rebuscada
  const [reloadKey, setReloadKey] = useState(0)

  const label = customLabel || 'Origem'
  // Mesma composição da ressalva de "Descrição do pagamento" nos modais de baixa:
  // rótulo, ícone de informação cinza e tooltip com seta. `bottom-start` porque
  // o rótulo fica colado na borda esquerda: para cima a tooltip cobriria o campo
  // anterior, e centralizada no ícone ela sangra para fora do modal — ancorada no
  // início, cresce para a direita, onde sobra espaço.
  const labelNode = labelTooltip ? (
    <Box display="flex" alignItems="center" gap="5px">
      {label}
      <Tooltip
        arrow
        enterTouchDelay={0}
        placement="bottom-start"
        title={labelTooltip}
      >
        <Box display="flex">
          <Icon icon="info" colorSX={theme.palette.neutral[600]} />
        </Box>
      </Tooltip>
    </Box>
  ) : (
    label
  )

  useEffect(() => {
    const fetchSourcesAutocomplete = async () => {
      const response = await DataService({
        type: 'GET',
        url: '/v1/accounts_payables/sources/autocomplete'
      })

      const records = response.data.records.map(
        (source: { name: any; uuid: any; source_type: any }) => ({
          label: source.name,
          value: source.uuid,
          source_type: source.source_type
        })
      )

      const creditCards = records.filter(
        (record: Record) => record.source_type === 'credit_card'
      )
      const bankAccounts = records.filter(
        (record: Record) => record.source_type === 'bank_account'
      )
      const others = records.filter(
        (record: Record) =>
          record.source_type === 'cash' || record.source_type == 'other'
      )

      setSources([
        ...(creditCards.length > 0
          ? [
              {
                label: 'Cartões de crédito',
                value: 'credit_card',
                source_type: 'credit_card'
              },
              ...creditCards
            ]
          : []),
        ...(bankAccounts.length > 0
          ? [
              {
                label: 'Contas bancárias',
                value: 'bank_account',
                source_type: 'bank_account'
              },
              ...bankAccounts
            ]
          : []),
        ...(others.length > 0
          ? [
              { label: 'Outros', value: 'other', source_type: 'other' },
              ...others
            ]
          : [])
      ])
    }

    fetchSourcesAutocomplete()
  }, [reloadKey])

  return (
    <>
      <AutocompleteAdd
        addPermission="accounts_payable:manage"
        key={value?.value}
        {...{ value, onChange, setValue, errorMessage }}
        addButtonText={customAddButtonText || 'Adicionar origem'}
        label={labelNode}
        placeholder={customPlaceholder || 'Selecione a origem do pagamento'}
        modalTitle={customModalTitle || 'Nova origem'}
        options={sources || []}
        getOptionDisabled={(option: any) =>
          ['credit_card', 'bank_account', 'other'].includes(option.value)
        }
        renderOption={(props, option) => {
          const optionTyped = option as Record
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { style, sx, onClick, ...rest } = props

          if (
            ['credit_card', 'bank_account', 'other'].includes(optionTyped.value)
          ) {
            return (
              <Box
                component="li"
                sx={{
                  all: 'unset',
                  bgcolor: theme.palette.neutral[700],
                  fontWeight: 600,
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
                aria-disabled={true}
                data-option-index={-1}
                {...rest}
              >
                {optionTyped.label}
              </Box>
            )
          }

          return (
            <Box
              component="li"
              sx={{ all: 'unset' }}
              onClick={onClick}
              {...rest}
            >
              {optionTyped.label}
            </Box>
          )
        }}
        modalBodyComponent={
          <SourceForm
            onCreated={(item) => {
              // seleciona o item novo no campo de quem usa o autocomplete
              onChange(null, { label: item.name, value: item.uuid })
              setReloadKey((key) => key + 1)
            }}
            entityLabel={entityLabel}
            handleClose={() => {
              return
            }}
          />
        }
      />
    </>
  )
}

export default SourceAutocomplete
